import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Mail, ArrowRight } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, senha);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email ou senha incorretos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-[52%] relative overflow-hidden"
        style={{
          backgroundImage: `url('/onibus_gugu.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Overlay gradient — levemente reduzido para mostrar o ônibus real */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(1,71,160,0.78) 0%, rgba(0,30,69,0.82) 100%)' }} />

        {/* Decorative circles */}
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full opacity-10" style={{ background: '#f9c900' }} />
        <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full opacity-5 border-4 border-white" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img
              src="/logotipo_gugu.png"
              alt="Gugu Turismo"
              className="w-14 h-14 rounded-2xl object-cover shadow-xl flex-shrink-0"
            />
            <div>
              <p className="text-white font-bold text-xl leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>Gugu Turismo</p>
              <p className="text-xs font-semibold tracking-widest uppercase mt-1" style={{ color: '#f9c900' }}>Sistema de Gestão</p>
            </div>
          </div>

          {/* Main copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 text-xs font-bold tracking-wider uppercase"
              style={{ background: 'rgba(249,201,0,0.15)', color: '#f9c900', border: '1px solid rgba(249,201,0,0.3)' }}>
              Plataforma Administrativa
            </div>
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gestão completa de excursões e transporte
            </h2>
            <p className="text-blue-100/70 text-base leading-relaxed max-w-sm">
              Clientes, viagens, reservas e encomendas — tudo em um único sistema profissional.
            </p>

            {/* Stats row */}
            <div className="flex gap-8 mt-10 pt-8 border-t border-white/10">
              {[
                { val: '100%', sub: 'Operacional' },
                { val: '24/7', sub: 'Disponível' },
                { val: 'MVP', sub: 'Pronto p/ uso' },
              ].map(s => (
                <div key={s.val}>
                  <p className="text-2xl font-bold" style={{ color: '#f9c900', fontFamily: 'Manrope, sans-serif' }}>{s.val}</p>
                  <p className="text-blue-200/60 text-xs mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <img
              src="/logotipo_gugu.png"
              alt="Gugu Turismo"
              className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
            />
            <div>
              <p className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Gugu Turismo</p>
              <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-widest">Sistema</p>
            </div>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bem-vindo de volta
            </h1>
            <p className="text-slate-500 text-sm">Entre com suas credenciais para acessar o sistema</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  placeholder="seu@email.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:border-[#0147a0] transition-all"
                  style={{ '--tw-ring-color': 'rgba(1,71,160,0.2)' }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:border-[#0147a0] transition-all"
                  style={{ '--tw-ring-color': 'rgba(1,71,160,0.2)' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full text-white font-bold py-3 rounded-xl transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-red-500/20 hover:shadow-red-500/30"
              style={{ background: loading ? '#d72d21' : 'linear-gradient(135deg, #d72d21, #ff3d31)' }}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  Entrar no sistema
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
